/**
 * 약관 / 개인정보 / 마케팅 / OSS 뷰어 — SubActivity 로 렌더.
 *
 * URL: /terms/view?type=service|privacy|marketing|oss
 *
 * ActivityHost 가 AppBar 와 스크롤 컨테이너를 제공하므로 여기서는 본문 + 하단 CTA 만 렌더.
 * `openActivity({ url: "/terms/view?type=..." })` 한 줄로 진입.
 *
 * 데이터 소스 (2026-05-22 변경):
 *   - service/privacy/marketing → 어드민(adbe) 의 TERMS_DIV 약관 본문(HTML)
 *     사용자 BE `GET /api/v1/user/terms/{divisionCode}/latest?lang=` 응답을 그대로 렌더.
 *     Redis `shared:terms:latest:{div}:{lang}` 캐시 — 어드민 CRUD 시 자동 evict.
 *     요청 언어의 번역본이 없으면 정본(한국어) 본문이 내려오고, 그때만 원문 안내를 띄운다.
 *   - oss → BE 약관 도메인과 무관, 정적 컨텐츠 (termsContent.ts).
 *   - 404 / BE 미가용 → 정적 fallback (termsContent.ts) 표시.
 */
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { TERMS_CONTENT, type TermsType } from "../../components/terms/termsContent";
import { useLatestTermsQuery } from "../../hooks/useTermsQueries";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { track } from "../../lib/tracking";
import { sanitizeHtml } from "../../lib/sanitizeHtml";
import { currentLanguage } from "../../i18n";
import styles from "./TermsViewerPage.module.css";

const VALID_TYPES: ReadonlyArray<TermsType> = ["service", "privacy", "marketing", "oss"];

/** TermsViewerPage URL ?type 값과 어드민 TERMS_DIV 공통코드 매핑. */
const TYPE_TO_DIVISION_CODE: Record<TermsType, string | null> = {
  service: "SERVICE",
  privacy: "PRIVACY",
  marketing: "MKTG",
  oss: null, // 정적 컨텐츠만 사용
};

function isTermsType(value: string | null): value is TermsType {
  return value !== null && VALID_TYPES.includes(value as TermsType);
}

export default function TermsViewerPage() {
  const [params] = useSearchParams();
  const raw = params.get("type");
  const type: TermsType = isTermsType(raw) ? raw : "service";
  const fallback = TERMS_CONTENT[type];
  const divisionCode = TYPE_TO_DIVISION_CODE[type];
  const nav = useActivityNavigator();
  const { t, i18n } = useTranslation();
  // i18n.language 를 의존성에 둬야 설정에서 언어를 바꿨을 때 다시 조회된다.
  const language = useMemo(() => currentLanguage(), [i18n.language]);

  // OSS 는 정적이라 fetch skip. 그 외 division 은 BE 약관 본문을 try.
  // useLatestTermsQuery 내부에서 404 는 null 로 fallback 처리됨.
  const { data: latest, isLoading } = useLatestTermsQuery(divisionCode ?? "", language);

  useEffect(() => {
    track("terms_view", { type });
  }, [type]);

  const bodyHtml = divisionCode ? latest?.bodyContent : null;
  const showLoading = divisionCode !== null && isLoading && !bodyHtml;
  // 번역본이 없어 정본이 내려온 경우에만 안내. 정적 fallback(=한국어 원문)일 때도 마찬가지다.
  const showOriginalNotice =
    language !== "ko" && divisionCode !== null && (!bodyHtml || latest?.languageCode !== language);

  return (
    <div className={styles.root}>
      {showOriginalNotice && !showLoading ? (
        <p className={styles.muted}>{t("terms.originalNotice")}</p>
      ) : null}

      {showLoading ? (
        <div className={styles.muted}>{t("terms.loading")}</div>
      ) : bodyHtml ? (
        <div
          className={styles.body}
          // BE sanitize + FE DOMPurify 이중 방어.
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }}
        />
      ) : (
        // 미등록 / 404 / BE 미가용 → 정적 plain-text fallback.
        <div className={styles.fallbackBody}>{fallback.body.trim()}</div>
      )}

      <div className={styles.footer}>
        <Button
          variant="primary"
          size="lg"
          label={t("action.confirm")}
          clickAction={() => nav.close()}
          width="100%"
        />
      </div>
    </div>
  );
}
